/**
 * LA JOURNÉE D'UN CAMION — refondue le 30/10/2026.
 *
 * Maquette validée par Mohamed le 30/10 : « l'écran je valide », après sept
 * itérations. Cadrage « Stafa v2 — Cadrage : la refonte de l'écran des
 * journées (30/10/2026) ».
 *
 * CE QU'IL A DEMANDÉ, MOT POUR MOT : « on declare le debut de journée qui doit
 * etre coehrant avec la fin de la journée d'avant. puis les etapes dans l'odre
 * du planning. on ajoute les km entre deux villes et un selectionneur pour
 * preciser si c'est a vide ou chargé. » Plus, à chaque itération :
 * « minimaliste », « agréable à utiliser », et « c'est un endroit ou on doit
 * pas faire d'erreur ».
 *
 *
 * QUATRE COLONNES AU LIEU DE ONZE
 * ---------------------------------------------------------------------------
 * Type · lieu → lieu · chargé/à vide · kilomètres · péage.
 *
 * CE QUI EST PARTI, ET POURQUOI :
 *   - « Arrivée », « Départ », « Attente » : les heures se saisissent déjà à la
 *     validation d'une étape depuis le lot 25. Les redemander ici était un
 *     doublon, et un doublon finit par ne plus dire la même chose ;
 *   - « À bord » : une quantité qui ne sert à aucun calcul de cet écran ;
 *   - « Imputé à » : une règle de répartition du péage NON TRANCHÉE, pas une
 *     information que l'exploitation détient. La colonne `peage_ot_id` reste en
 *     base et n'est plus posée d'ici ;
 *   - « est. » : l'estimation se lisait comme un relevé.
 *
 * L'ARRÊT EST UN REPÈRE, PAS UNE LIGNE À REMPLIR. Fond teinté, aucun champ :
 * la seule ligne qui se saisit est la ROUTE. C'est ce qui rend la colonne de
 * saisie continue de haut en bas — on descend sans chercher où taper.
 *
 *
 * LA BANDE DE TOTAUX ET LE DÉPLIANT
 * ---------------------------------------------------------------------------
 * Kilomètres · à vide · part · chiffre d'affaires · MARGE. C'est le but de tout
 * le chantier : la marge d'une journée, à l'endroit où on la saisit.
 *
 * LE DÉPLIANT « LE CALCUL » EST REPLIÉ PAR DÉFAUT — demande du 30/10 : « il
 * faut un endroit discret dépliable avec les calculs ». Ouvert, il refait le
 * montant ligne à ligne. Un total que personne ne peut refaire est un total
 * qu'on finit par ne plus croire.
 *
 * AUCUN TEXTE EXPLICATIF À L'ÉCRAN — « tu met trop de commentaire,
 * minimaliste ». Ce qui reste sont des ALERTES, pas des commentaires.
 *
 *
 * UN COMPOSANT À PROPRIÉTÉS, comme avant : il n'appelle rien lui-même. C'est ce
 * qui permet de le RENDRE hors API dans `apercu.tsx`, et de le regarder avant
 * de le livrer.
 *
 *
 * LA SAISIE EST « À LA SORTIE DU CHAMP », PAS À CHAQUE FRAPPE
 * ---------------------------------------------------------------------------
 * Un enregistrement par caractère ferait douze écritures pour « 78 », dont onze
 * fausses — et la dernière pourrait arriver avant l'avant-dernière. On garde
 * donc la frappe en mémoire et on n'écrit qu'au `blur`, et seulement si la
 * valeur a changé.
 */

import { useEffect, useState } from 'react';
import { ChoixCherchable } from './ChoixCherchable.js';
import { nommerLeSite } from './EcranOrdres.js';
/**
 * `euros` VIENT DE `resultat.js` ET NON DE `finance.js` — correction du 30/10.
 *
 * LE DÉFAUT, TROUVÉ EN REGARDANT L'ÉCRAN ET PAR RIEN D'AUTRE : l'aperçu
 * affichait « 38 000,00 € » là où la maquette dit 380,00. Les deux fonctions
 * portent le même nom et ne prennent pas la même unité — celle de `finance.js`
 * attend des EUROS, celle-ci des CENTIMES, et tout ce que la route rend est en
 * centimes.
 *
 * SEPT FONCTIONS `euros` COEXISTENT DANS L'INTERFACE (finance, resultat, bfr,
 * confirmation, echeancier, previsions, et deux variantes dans tresorerie).
 * C'est une dette du dépôt, pas une décision ; elle est signalée dans le
 * message du lot. Ici on prend celle qui parle la même unité que l'API.
 *
 * Elle ne pose PAS le symbole « € » : les appelants l'ajoutent, comme
 * `EcranResultat` le fait déjà.
 */
import { euros } from './resultat.js';
import type { LigneListe, ResultatDeLaJournee } from './api.js';
import {
  chaineDeLaJournee,
  deplacerArret,
  enChargeEffectif,
  explicationDePart,
  forcageChoisi,
  jourDit,
  totauxDeLaJournee,
  type ArretDeLaJournee,
  type EnTeteJournee,
  type LigneDeChaine,
  type OrdreDuJour,
  type SegmentLu,
} from './journees.js';

export type { PartDeService, ResultatDeLaJournee } from './api.js';

export interface DonneesJournee {
  readonly journee: EnTeteJournee;
  readonly etapes: readonly ArretDeLaJournee[];
  readonly segments: readonly SegmentLu[];
  readonly ordres: readonly OrdreDuJour[];
  /** `null` quand la journée n'a aucun arrêt : elle n'existe pas pour le calcul. */
  readonly resultat: ResultatDeLaJournee | null;
}

export type EtatJourneeCamion =
  | { readonly nom: 'chargement' }
  | { readonly nom: 'panne' }
  | { readonly nom: 'prete'; readonly donnees: DonneesJournee };

/** Ce qu'on peut écrire sur un trajet. Tous facultatifs : on n'envoie que ce
 *  qui change, et `null` efface. */
export interface ChampsTrajet {
  readonly km_reels?: number | null;
  readonly peage?: string | null;
  readonly peage_ot_id?: string | null;
  /**
   * `null` RAMÈNE À LA DÉDUCTION, et ce n'est pas la même chose que `false`.
   * Sans ce troisième état, un forçage fautif resterait pour toujours.
   */
  readonly en_charge?: boolean | null;
}

export interface Gestes {
  readonly quandRetour: () => void;
  /** UN SEUL GESTE POUR LE TRAJET — kilomètres, nature et péage vivent sur la
   *  même ligne de base (`segments`), et les séparer ferait trois écritures là
   *  où une suffit. */
  readonly quandTrajet: (
    depuis: string | null,
    vers: string | null,
    champs: ChampsTrajet,
  ) => void;
  readonly quandDebut: (siteId: string | null) => void;
  readonly quandFin: (siteId: string | null) => void;
  readonly quandConsommation: (valeur: string | null) => void;
  readonly quandOrdonner: (etapes: readonly string[]) => void;
  readonly quandPointer: (pointee: boolean) => void;
  readonly quandCloturer: (cloturee: boolean) => void;
}

/** Un nombre en français, ou un tiret. Jamais un zéro qui ferait croire à une mesure. */
function nombre(valeur: number | null, decimales = 0): string {
  if (valeur === null || !Number.isFinite(valeur)) return '—';
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: decimales,
    maximumFractionDigits: decimales,
  }).format(valeur);
}

/**
 * LES MONTANTS SE SAISISSENT EN FRANÇAIS.
 *
 * L'API rend « 6.80 » : c'est une valeur de TRANSPORT, pas d'affichage. La
 * poser telle quelle mettrait un point décimal anglo-saxon sous les yeux de
 * quelqu'un qui tape des virgules toute la journée — le défaut du 11/09,
 * retrouvé au rendu du 14/09.
 */
function versLaSaisie(valeur: string | null): string {
  return valeur === null ? '' : valeur.replace('.', ',');
}

function versLApi(frappe: string): string | null {
  const propre = frappe.trim().replace(',', '.');
  return propre === '' ? null : propre;
}

/**
 * Une case de saisie qui n'écrit qu'à la sortie du champ.
 *
 * ELLE SE RESYNCHRONISE QUAND LA VALEUR VENUE DE L'API CHANGE — sans cela, un
 * refus laisserait à l'écran le chiffre refusé, et l'utilisateur croirait
 * l'avoir enregistré.
 */
function CaseSaisie({
  valeur,
  gabarit,
  desactivee,
  quandValide,
  libelle,
}: {
  readonly valeur: string;
  readonly gabarit?: string;
  readonly desactivee?: boolean;
  readonly quandValide: (v: string) => void;
  /** Le nom lu par un lecteur d'écran quand l'unité seule est affichée. */
  readonly libelle?: string;
}) {
  const [frappe, setFrappe] = useState(valeur);
  useEffect(() => {
    setFrappe(valeur);
  }, [valeur]);

  return (
    <input
      className="jrn-case"
      aria-label={libelle}
      value={frappe}
      placeholder={gabarit}
      disabled={desactivee === true}
      onChange={(e) => {
        setFrappe(e.target.value);
      }}
      onBlur={() => {
        if (frappe !== valeur) quandValide(frappe);
      }}
    />
  );
}

export function VueJourneeDuCamion({
  jour,
  etat,
  sites,
  refus,
  gestes,
}: {
  readonly jour: string;
  readonly etat: EtatJourneeCamion;
  /** L'annuaire des sites, pour choisir un lieu de début ou de fin. */
  readonly sites: readonly LigneListe[];
  /** Le dernier refus de l'API, déjà mis en français. `null` quand tout va bien. */
  readonly refus: string | null;
  readonly gestes: Gestes;
}) {
  /**
   * DEUX ÉTATS LOCAUX, ET RIEN D'AUTRE.
   *
   * Le départ ne montre son sélecteur qu'après un clic sur « Changer » : neuf
   * fois sur dix il est juste, et un champ ouvert invite à y toucher.
   * Le dépliant est REPLIÉ par défaut — demande du 30/10.
   */
  const [changerDebut, setChangerDebut] = useState(false);
  const [calculOuvert, setCalculOuvert] = useState(false);
  /** Le détail du chiffre d'affaires, replié par défaut — lot 143. */
  const [caOuvert, setCaOuvert] = useState(false);

  if (etat.nom !== 'prete') {
    return (
      <div className="corps corps-ot">
        <div className="barre-outils">
          <button type="button" className="lien-action" onClick={gestes.quandRetour}>
            ‹ Retour
          </button>
          <span className="etiquette">{jourDit(jour)}</span>
        </div>
        {etat.nom === 'chargement' && <p className="attente">Lecture de la journée…</p>}
        {etat.nom === 'panne' && (
          <p className="bandeau-refus">Cette journée n’a pas pu être lue.</p>
        )}
      </div>
    );
  }

  const { journee, etapes, segments, ordres, resultat } = etat.donnees;
  const detailCa = resultat?.detail_ca ?? [];
  const chaine = chaineDeLaJournee(journee, etapes, segments);
  const totaux = totauxDeLaJournee(journee, etapes, chaine);
  const figee = journee.cloturee_le !== null;

  const trajets = chaine.lignes.filter(
    (l): l is LigneDeChaine => l.sorte === 'trajet',
  );
  /** L'ordre des arrêts tel qu'affiché — la base du geste « monter / descendre ». */
  const ordreDesArrets = chaine.lignes.flatMap((l) =>
    l.sorte === 'arret' ? [l.etape.id] : [],
  );
  const deplacer = (etapeId: string, sens: -1 | 1) => {
    const suite = deplacerArret(ordreDesArrets, etapeId, sens);
    if (suite !== null) gestes.quandOrdonner(suite);
  };
  const releves = trajets.length - totaux.trajets_sans_km;

  /**
   * LA PART À VIDE SE CALCULE SUR LES KILOMÈTRES SAISIS, pas sur le total.
   *
   * `part_a_vide` vaut `null` tant qu'un trajet manque — c'est juste pour un
   * TOTAL. Mais pendant la saisie, la part de ce qui est déjà tapé est une
   * information utile, et c'est la décision du 30/10 : « kilomètres et coût
   * oui, marge non ».
   */
  const videSaisis = totaux.km_saisis - totaux.km_en_charge_saisis;
  const partVide =
    totaux.km_saisis === 0 ? null : (videSaisis / totaux.km_saisis) * 100;

  /** La marge en couleur, et elle seule — tout colorer ne mettrait rien en valeur. */
  const classeMarge =
    resultat?.resultat_centimes == null
      ? 'pt-tuile'
      : resultat.resultat_centimes < 0
        ? 'pt-tuile pt-perte'
        : 'pt-tuile pt-gain';

  return (
    /* NOUVEAU STYLE — maquette « Journée d'un camion — pointage » validée par
       Mohamed le 02/11/2026 (lot 135). SEULE LA PRÉSENTATION CHANGE : mêmes
       champs, mêmes gestes, mêmes alertes. Le tableau devient un fil vertical,
       les totaux passent dans un bloc à droite. */
    <div className="corps corps-ot pointage">
      <div className="barre-outils">
        <button type="button" className="lien-action" onClick={gestes.quandRetour}>
          ‹ Camions du jour
        </button>
        <span className="etiquette">{jourDit(jour)}</span>
      </div>

      {/* L'EN-TÊTE : qui, quoi, et OÙ ON EN EST. Le compteur « 4 / 5 » dit
          combien de routes sont relevées — c'est la seule chose qu'on cherche
          en ouvrant cet écran. */}
      <div className="pt-tete">
        <div className="pt-qui-bloc">
          <h2 className="pt-titre">{journee.immatriculation}</h2>
          <span className="pt-qui">
            {journee.chauffeurs.length === 0
              ? 'sans chauffeur'
              : journee.chauffeurs.join(', ')}
            {journee.remorques.length > 0 &&
              ` · remorque ${journee.remorques.join(' puis ')}`}
          </span>
        </div>
        {figee ? (
          <span className="pt-etat pt-etat-close">Clôturée</span>
        ) : (
          trajets.length > 0 && (
            <span
              className={
                totaux.trajets_sans_km === 0 ? 'pt-etat pt-etat-fini' : 'pt-etat'
              }
            >
              {releves} / {trajets.length} trajets
            </span>
          )
        )}
        <span className="pt-actions">
          {figee ? (
            <button
              type="button"
              className="bouton bouton-discret"
              onClick={() => {
                gestes.quandCloturer(false);
              }}
            >
              Rouvrir
            </button>
          ) : (
            <>
              <button
                type="button"
                className="bouton bouton-discret"
                onClick={() => {
                  gestes.quandPointer(journee.pointee_le === null);
                }}
              >
                {journee.pointee_le === null ? 'Pointer' : 'Dépointer'}
              </button>
              <button
                type="button"
                className="bouton"
                disabled={journee.pointee_le === null || journee.fin_site_id === null}
                onClick={() => {
                  gestes.quandCloturer(true);
                }}
              >
                Clôturer
              </button>
            </>
          )}
        </span>
      </div>

      {/* UN REFUS SE DIT. Sans ce bandeau, une clôture refusée ne changerait
          rien à l'écran, et l'utilisateur croirait avoir cliqué à côté. */}
      {refus !== null && <p className="bandeau bandeau-refus">{refus}</p>}

      {chaine.caducs.length > 0 && (
        <p className="bandeau bandeau-attente">
          {chaine.caducs.length === 1
            ? 'Un relevé kilométrique ne correspond plus à aucun trajet'
            : `${chaine.caducs.length} relevés kilométriques ne correspondent plus à aucun trajet`}{' '}
          : les arrêts ont été réordonnés depuis. Ils ne sont comptés nulle part.
        </p>
      )}

      <div className="pt-colonnes">
        <section className="fn-bloc pt-fil-bloc" aria-label="Arrêts et trajets">
          <h3 className="titre-bloc-ot">Arrêts et trajets</h3>

          <div className="pt-fil">
            {/* LE DÉBUT DOIT ÊTRE COHÉRENT AVEC LA FIN DE LA VEILLE — c'est la
                première phrase de la demande du 30/10. Trois cas, trois tons. */}
            <div className="pt-arret pt-borne">
              <span className="pt-noeud pt-noeud-plein" aria-hidden="true" />
              <div className="pt-arret-texte">
                <span className="pt-genre">Début</span>
                <span className="pt-lieu">{journee.depart_nom ?? 'lieu inconnu'}</span>
                {journee.veille_sans_fin ? (
                  <span className="jrn-alerte">
                    la journée précédente ne dit pas où ce camion s’est arrêté
                  </span>
                ) : journee.depart_corrige ? (
                  <span className="jrn-alerte">
                    départ corrigé — la journée précédente, elle, n’est pas corrigée
                  </span>
                ) : journee.depart_repris_du !== null ? (
                  <span className="jrn-accord">
                    ✓ repris du {jourDit(journee.depart_repris_du)}
                  </span>
                ) : (
                  <span className="jrn-accord">✓ dépôt</span>
                )}
                {!figee && changerDebut && (
                  <span className="jrn-lieu">
                    <ChoixCherchable
                      id="jrn-debut"
                      choix={sites.map((s) => ({ id: s.id, libelle: nommerLeSite(s) }))}
                      valeur={journee.debut_site_id ?? ''}
                      invite={journee.depart_nom ?? 'lieu de départ'}
                      quandChoisir={(id) => {
                        gestes.quandDebut(id === '' ? null : id);
                        setChangerDebut(false);
                      }}
                    />
                  </span>
                )}
              </div>
              {!figee && !changerDebut && (
                <button
                  type="button"
                  className="lien-action"
                  onClick={() => {
                    setChangerDebut(true);
                  }}
                >
                  Changer
                </button>
              )}
            </div>

            {etapes.length === 0 && (
              <p className="vide pt-vide">Aucun arrêt ce jour-là pour ce camion.</p>
            )}

            {chaine.lignes.map((ligne) =>
              ligne.sorte === 'trajet' ? (
                <LigneDeRoute
                  key={`t-${String(ligne.depuis_etape_id)}-${String(ligne.vers_etape_id)}`}
                  trajet={ligne}
                  figee={figee}
                  quandKm={(km) => {
                    gestes.quandTrajet(ligne.depuis_etape_id, ligne.vers_etape_id, {
                      km_reels: km,
                    });
                  }}
                  quandPeage={(peage) => {
                    // EFFACER LE MONTANT EFFACE LA CIBLE : la base refuse une
                    // cible sans montant, et laisser découvrir ce refus pour
                    // un geste qui a manifestement ce sens serait une chicane.
                    gestes.quandTrajet(
                      ligne.depuis_etape_id,
                      ligne.vers_etape_id,
                      peage === null ? { peage: null, peage_ot_id: null } : { peage },
                    );
                  }}
                  quandNature={(en) => {
                    gestes.quandTrajet(ligne.depuis_etape_id, ligne.vers_etape_id, {
                      en_charge: en,
                    });
                  }}
                />
              ) : (
                /* L'ARRÊT EST UN REPÈRE : aucun champ. On le lit en passant, on
                   ne s'y arrête pas. */
                <div key={ligne.etape.id} className="pt-arret">
                  <span className="pt-noeud" aria-hidden="true" />
                  <div className="pt-arret-texte">
                    <span className="pt-genre">
                      {ligne.etape.type === 'chargement' ? 'Chargement' : 'Livraison'}
                    </span>
                    <span className="pt-lieu">
                      {ligne.etape.site_ville ?? ligne.etape.site_nom}
                    </span>
                    <span className="pt-sous">
                      {ligne.etape.client_nom} · {ligne.etape.ot_numero}
                    </span>
                  </div>
                  {/* REMETTRE L'ARRÊT À SON RANG — lot 139. Monter / descendre
                      d'un cran ; la route reçoit la liste complète. */}
                  {!figee && ordreDesArrets.length > 1 && (
                    <span className="pt-ordre">
                      <button
                        type="button"
                        aria-label={`Monter l’arrêt ${ligne.etape.site_ville ?? ligne.etape.site_nom ?? ''}`}
                        disabled={ordreDesArrets[0] === ligne.etape.id}
                        onClick={() => {
                          deplacer(ligne.etape.id, -1);
                        }}
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        aria-label={`Descendre l’arrêt ${ligne.etape.site_ville ?? ligne.etape.site_nom ?? ''}`}
                        disabled={ordreDesArrets[ordreDesArrets.length - 1] === ligne.etape.id}
                        onClick={() => {
                          deplacer(ligne.etape.id, 1);
                        }}
                      >
                        ↓
                      </button>
                    </span>
                  )}
                </div>
              ),
            )}

            <div className="pt-arret pt-borne">
              <span className="pt-noeud pt-noeud-plein" aria-hidden="true" />
              <div className="pt-arret-texte">
                <span className="pt-genre">Fin</span>
                <span className="jrn-lieu pt-fin">
                  <ChoixCherchable
                    id="jrn-fin"
                    choix={sites.map((s) => ({ id: s.id, libelle: nommerLeSite(s) }))}
                    valeur={journee.fin_site_id ?? ''}
                    invite="Où le camion s’est arrêté…"
                    desactive={figee}
                    quandChoisir={(id) => {
                      gestes.quandFin(id === '' ? null : id);
                    }}
                  />
                </span>
                {journee.fin_site_id === null && (
                  <span className="jrn-alerte">
                    sans lui, la journée suivante ne peut pas partir
                  </span>
                )}
              </div>
            </div>
          </div>
        </section>

        <aside className="pt-cote">
          {/* LA JOURNÉE EN QUATRE CHIFFRES — le but de tout le chantier. */}
          <section className="fn-bloc" aria-label="La journée">
            <h3 className="titre-bloc-ot">La journée</h3>
            <div className="pt-tuiles">
              <span className="pt-tuile">
                <b>{nombre(totaux.km_saisis)}</b>
                <span>km</span>
              </span>
              <span className="pt-tuile">
                <b>{nombre(videSaisis)}</b>
                <span>
                  km à vide{partVide === null ? '' : ` · ${nombre(partVide)} %`}
                </span>
              </span>
              {/* LE CHIFFRE D'AFFAIRES SE DÉPLIE AU CLIC — lot 143 : les OT
                  de la journée et la part de chacun. */}
              {detailCa.length > 0 ? (
                <button
                  type="button"
                  className="pt-tuile pt-tuile-bouton"
                  aria-expanded={caOuvert}
                  onClick={() => {
                    setCaOuvert(!caOuvert);
                  }}
                >
                  <b>{euros(resultat?.ca_centimes)} €</b>
                  <span>chiffre d’affaires {caOuvert ? '▴' : '▾'}</span>
                </button>
              ) : (
                <span className="pt-tuile">
                  <b>{euros(resultat?.ca_centimes)} €</b>
                  <span>chiffre d’affaires</span>
                </span>
              )}
              <span className={classeMarge}>
                <b>{euros(resultat?.resultat_centimes)} €</b>
                <span>marge</span>
              </span>
            </div>

            {caOuvert && detailCa.length > 0 && (
              <div className="jrn-calcul-detail pt-calcul" aria-label="Le chiffre d’affaires, OT par OT">
                <div className="jrn-calcul-groupe">
                  <span className="jrn-calcul-titre">Ce qui rentre, OT par OT</span>
                  {detailCa.map((p) => {
                    const o = ordres.find((x) => x.id === p.ot_id);
                    return (
                      <div key={p.ot_id} className="pt-part-ca">
                        <span className="jrn-calcul-ligne">
                          <span>
                            {o === undefined ? 'OT' : `${o.numero} · ${o.client_nom}`}
                          </span>
                          <b>{euros(p.part_centimes)} €</b>
                        </span>
                        <span className="pt-part-explication">
                          {explicationDePart(p, (c) => euros(c))}
                        </span>
                      </div>
                    );
                  })}
                  <span className="jrn-calcul-ligne jrn-calcul-sous-total">
                    <span>Chiffre d’affaires de la journée</span>
                    <b>{euros(resultat?.ca_centimes)} €</b>
                  </span>
                </div>
              </div>
            )}

            {/* POURQUOI LA MARGE MANQUE, quand elle manque. Une alerte, pas un
                commentaire : sans elle, un « — » enverrait chercher une panne là
                où il manque une saisie. */}
            {resultat !== null && resultat.pourquoi_sans_ca !== null && (
              <p className="bandeau bandeau-attente">{resultat.pourquoi_sans_ca}</p>
            )}
            {resultat !== null && resultat.pourquoi_sans_cout !== null && (
              <p className="bandeau bandeau-attente">{resultat.pourquoi_sans_cout}</p>
            )}
            {resultat?.parametrage_manquant.map((m) => (
              <p key={m} className="bandeau bandeau-attente">
                {m}
              </p>
            ))}

            {/* LE DÉPLIANT « LE CALCUL » — replié par défaut, demandé le 30/10 :
                « il faut un endroit discret dépliable avec les calculs ». */}
            {resultat?.depenses != null && (
              <>
                <button
                  type="button"
                  className="lien-action pt-calcul-bouton"
                  aria-expanded={calculOuvert}
                  onClick={() => {
                    setCalculOuvert(!calculOuvert);
                  }}
                >
                  {calculOuvert ? 'Masquer le calcul' : 'Le calcul'}
                </button>
                {calculOuvert && (
                  <div className="jrn-calcul-detail pt-calcul">
                    <LeCalcul
                      resultat={resultat}
                      depenses={resultat.depenses}
                      kmSaisis={totaux.km_saisis}
                    />
                  </div>
                )}
              </>
            )}
          </section>

          {/* LA CONSOMMATION — un réglage de la journée, pas une saisie d'arrêt.
              Elle PRIME sur la référence du service quand elle est posée. */}
          <section className="fn-bloc pt-conso" aria-label="Consommation du jour">
            <div className="pt-conso-ligne">
              <span className="pt-conso-titre">Consommation du jour</span>
              <label className="pt-unite pt-unite-large">
                <CaseSaisie
                  valeur={versLaSaisie(journee.consommation)}
                  gabarit="—"
                  libelle="Consommation du jour, en litres aux 100 km"
                  desactivee={figee}
                  quandValide={(v) => {
                    gestes.quandConsommation(versLApi(v));
                  }}
                />
                <span aria-hidden="true">L/100 km</span>
              </label>
            </div>
            {ordres.length > 0 && (
              <span className="sourdine">
                {ordres.length === 1 ? '1 transport' : `${ordres.length} transports`}
              </span>
            )}
          </section>
        </aside>
      </div>
    </div>
  );
}

/**
 * LE DÉTAIL DU CALCUL — ce qui rentre, ce qui sort, et la soustraction.
 *
 * CHAQUE LIGNE PORTE SON OPÉRATION, pas seulement son résultat : « 57,6 L ×
 * 1,2687 » se refait de tête, « 73,08 » se croit ou ne se croit pas.
 */
function LeCalcul({
  resultat,
  depenses,
  kmSaisis,
}: {
  readonly resultat: ResultatDeLaJournee;
  readonly depenses: NonNullable<ResultatDeLaJournee['depenses']>;
  readonly kmSaisis: number;
}) {
  return (
    <>
      <div className="jrn-calcul-groupe">
        <span className="jrn-calcul-titre">Ce qui rentre</span>
        <span className="jrn-calcul-ligne">
          <span>Chiffre d’affaires des transports</span>
          <b>{euros(resultat.ca_centimes)} €</b>
        </span>
      </div>

      <div className="jrn-calcul-groupe">
        <span className="jrn-calcul-titre">La journée d’attelage</span>
        {/* D'OÙ VIENT LE COÛT, SERVICE PAR SERVICE. Sur une journée mêlée, la
            part dit pourquoi le montant n'est ni l'un ni l'autre des deux
            coûts saisis. */}
        {depenses.parts.map((p) => (
          <span key={p.centre_id} className="jrn-calcul-ligne">
            <span>
              {p.nom}
              {depenses.parts.length > 1 && ` · ${nombre(p.part * 100)} %`}
            </span>
            <b>
              {depenses.parts.length > 1
                ? `${euros(p.cout_journalier_centimes)} € × ${nombre(p.part * 100)} %`
                : `${euros(p.cout_journalier_centimes)} €`}
            </b>
          </span>
        ))}
        <span className="jrn-calcul-ligne jrn-calcul-sous-total">
          <span>Coût de la journée</span>
          <b>{euros(depenses.journee)} €</b>
        </span>
      </div>

      <div className="jrn-calcul-groupe">
        <span className="jrn-calcul-titre">
          Ce que les {nombre(kmSaisis)} km ont coûté
        </span>
        <span className="jrn-calcul-ligne">
          <span>Gazole</span>
          <b>{euros(depenses.gazole)} €</b>
        </span>
        <span className="jrn-calcul-ligne">
          <span>Pneus et entretien</span>
          <b>{euros(depenses.frais_km)} €</b>
        </span>
        {depenses.frais_heure > 0 && (
          <span className="jrn-calcul-ligne">
            <span>Frais à l’heure</span>
            <b>{euros(depenses.frais_heure)} €</b>
          </span>
        )}
        <span className="jrn-calcul-ligne">
          <span>Péage</span>
          <b>{euros(depenses.peage)} €</b>
        </span>
      </div>

      <div className="jrn-calcul-groupe">
        <span className="jrn-calcul-ligne jrn-calcul-sous-total">
          <span>Coût total</span>
          <b>{euros(depenses.total)} €</b>
        </span>
        {/* LE PRIX AU KILOMÈTRE — un des garde-fous du cadrage : il confronte
            les kilomètres au coût, et un chiffre absurde saute aux yeux. */}
        {kmSaisis > 0 && (
          <span className="jrn-calcul-ligne sourdine">
            <span>soit au kilomètre</span>
            <b>{euros(Math.round(depenses.total / kmSaisis))} €</b>
          </span>
        )}
        <span className="jrn-calcul-ligne jrn-calcul-total">
          <span>Marge</span>
          <b>{euros(resultat.resultat_centimes)} €</b>
        </span>
      </div>

      {/* CE QUI MANQUE POUR QUE LE COÛT SOIT COMPLET. Un coût partiel annoncé
          comme complet est pire qu'un coût absent. */}
      {depenses.manques.length > 0 && (
        <p className="jrn-calcul-manques">
          Il manque {depenses.manques.join(', ')}.
        </p>
      )}
    </>
  );
}

/**
 * LA ROUTE ENTRE DEUX ARRÊTS — la seule ligne qui se saisit.
 *
 * Elle n'a ni poignée ni rang : on ne déplace pas un trajet, il se déduit des
 * arrêts qu'il relie.
 */
function LigneDeRoute({
  trajet,
  figee,
  quandKm,
  quandPeage,
  quandNature,
}: {
  readonly trajet: LigneDeChaine;
  readonly figee: boolean;
  readonly quandKm: (km: number | null) => void;
  readonly quandPeage: (peage: string | null) => void;
  readonly quandNature: (en: boolean | null) => void;
}) {
  const enCharge = enChargeEffectif(trajet);

  /**
   * LE DOUBLE BOUTON EST PRÉ-REMPLI PAR LA DÉDUCTION et modifiable — le juste
   * par défaut, l'exception possible. Ce qu'un appui envoie est décidé par
   * `forcageChoisi` (`journees.ts`, éprouvé par ses tests) : choisir la valeur
   * déduite efface le forçage.
   *
   * REVIREMENT ASSUMÉ DU 08/09, où la règle était « personne ne peut déclarer
   * en charge un trajet qui ne l'est pas ». La déduction ne sait pas tout : un
   * retour de palettes, un transfert entre dépôts, une reprise chez un confrère
   * ne sont portés par aucun ordre.
   */
  const choisir = (charge: boolean) => {
    const envoi = forcageChoisi(trajet, charge);
    if (envoi !== undefined) quandNature(envoi);
  };

  return (
    <div className="pt-route">
      <span className="pt-trajet">
        {trajet.depuis_nom} → {trajet.vers_nom}
      </span>
      <span
        className="pt-seg"
        role="group"
        aria-label={`Nature du trajet ${trajet.depuis_nom} → ${trajet.vers_nom}`}
      >
        <button
          type="button"
          className={enCharge ? '' : 'pt-seg-on'}
          aria-pressed={!enCharge}
          disabled={figee}
          onClick={() => {
            choisir(false);
          }}
        >
          À vide
        </button>
        <button
          type="button"
          className={enCharge ? 'pt-seg-on' : ''}
          aria-pressed={enCharge}
          disabled={figee}
          onClick={() => {
            choisir(true);
          }}
        >
          Chargé
        </button>
      </span>
      {/* AUCUN GABARIT CHIFFRÉ : au rendu du 14/09, l'estimation posée en
          gabarit se lisait comme un relevé — un trajet non pointé avait l'air
          pointé. Un trajet sans kilomètres est cerné d'orange. */}
      <label
        className={
          trajet.km_reels === null && !figee ? 'pt-unite pt-manque' : 'pt-unite'
        }
      >
        <CaseSaisie
          valeur={trajet.km_reels === null ? '' : String(trajet.km_reels)}
          gabarit="—"
          libelle="Kilomètres"
          desactivee={figee}
          quandValide={(v) => {
            const propre = v.trim();
            if (propre === '') {
              quandKm(null);
              return;
            }
            const n = Number(propre);
            // UNE SAISIE FAUTIVE N'EST PAS ENVOYÉE : l'API la refuserait, et
            // l'écran afficherait « données invalides » pour une lettre tapée
            // par mégarde. On la laisse à corriger.
            if (Number.isInteger(n) && n >= 0) quandKm(n);
          }}
        />
        <span aria-hidden="true">km</span>
      </label>
      <label className="pt-unite">
        <CaseSaisie
          valeur={versLaSaisie(trajet.peage)}
          gabarit="—"
          libelle="Péage"
          desactivee={figee}
          quandValide={(v) => {
            quandPeage(versLApi(v));
          }}
        />
        <span aria-hidden="true">€</span>
      </label>
    </div>
  );
}
